import { createFileRoute, Link } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  Clock,
  Eye,
  Loader2,
  RefreshCw,
  type LucideIcon,
} from "lucide-react";
import {
  AdminRole,
  DEFAULT_PAGE_SIZE,
  InvoicePolicyAction,
  InvoiceStatus,
  InvoiceSubStatus,
  TransientWalletStatus,
  trimDecimalZeros,
} from "@cpg/common";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { columnFilter, enumOptions, SelectFilterField } from "@/components/column-filter";
import { StatusBadge } from "@/components/status-badge";
import { Amount, CryptoFiatAmount, formatUsdAmount } from "@/components/amount";
import { AddressWithVault } from "@/components/address-with-vault";
import { HashLink } from "@/components/hash-link";
import { AssetLabel } from "@/components/asset-label";
import { BalanceBreakdownDialog } from "@/components/balance-breakdown-dialog";
import { StatCard } from "@/components/stat-card";
import { CopyButton } from "@/components/copy-button";
import { cn, formatStatusLabel } from "@/lib/utils";
import { hasRole } from "@/lib/roles";
import {
  ACCOUNT_STATUS_COLOR,
  API_KEY_STATUS_COLOR,
  INVOICE_STATUS_COLOR,
  MERCHANT_STATUS_COLOR,
  STATUS_DOT_COLOR,
  TRANSIENT_WALLET_STATUS_COLOR,
  WEBHOOK_DELIVERY_STATUS_COLOR,
  WEBHOOK_EVENT_COLOR,
  WITHDRAWAL_STATUS_COLOR,
} from "@/lib/status-colors";
import { ApiError } from "@/api/client";
import { listInvoices } from "@/api/invoices";
import { listWithdrawals } from "@/api/withdrawals";
import {
  getMerchant,
  getMerchantAccounts,
  getMerchantActivity,
  getMerchantApiKeys,
  getMerchantAssets,
  getMerchantLedgers,
  getMerchantPolicies,
  getMerchantStats,
  getMerchantTariff,
  getMerchantTariffOptions,
  getMerchantTransientWallets,
  getMerchantWebhook,
  getMerchantWebhookDeliveries,
  setMerchantAssetDisabled,
  updateMerchant,
} from "@/api/merchants";
import {
  type InvoiceListItem,
  type MerchantActivityEvent,
  type MerchantApiKeyItem,
  type MerchantPolicyItem,
  type MerchantResponse,
  type MerchantTariffOptionItem,
  type MerchantTransientWalletItem,
  type WebhookDeliveryListItem,
  type WithdrawalListItem,
} from "@/api/types";
import { type LedgerDrillDownTarget, LedgerTransactionsDialog } from "./-ledger-transactions-dialog";

// Real implementation of Task 25 Part D — the merchant detail page, ten
// tabs. Fetches the merchant once at the page level (header + Overview's
// tariffId); every other tab fetches its own sub-resource independently via
// its own `useQuery`, `enabled` only when that tab is active AND the admin
// holds the role gating it — mirrors Task 24 dashboard's "skip the query
// entirely rather than compute-then-hide" pattern. A tab whose role the
// admin lacks is omitted from the tab list entirely, same "omit, don't
// disable" rule.
//
// No `tabs.tsx` primitive exists yet in `components/ui/` — `TabNav` below is
// a small route-local pill button-group (styled like `period-switcher.tsx`),
// not worth adding a new shared shadcn primitive for a single page.
export const Route = createFileRoute("/_app/merchants_/$merchantId")({
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  component: MerchantDetailPage,
});

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function BlockStatus({ label }: { label: string }) {
  return <div className="text-sm text-muted-foreground">{label}</div>;
}

function SectionCard({
                       title,
                       description,
                       action,
                       children,
                     }: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        {action}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

const SELECT_CLASSNAME = cn(
  "h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

// ─── tab config + nav ────────────────────────────────────────────────────────

type TabKey =
  | "overview"
  | "transient-wallets"
  | "accounts"
  | "invoices"
  | "withdrawals"
  | "assets"
  | "policies"
  | "api-keys"
  | "activity";

interface TabConfig {
  key: TabKey;
  label: string;
  role: AdminRole;
  /** Set when the tab is visible under EITHER `role` OR this one — see the Withdrawals tab's comment. */
  altRole?: AdminRole;
}

// `ledger` merges the old `accounts` (MERCHANTS_VIEWER) and `ledgers`
// (FINANCE_VIEWER) tabs into one table — the backend still gates
// `GET :id/accounts` at MERCHANTS_VIEWER and `GET :id/ledgers` at
// FINANCE_VIEWER (deliberate separation of powers, per
// merchants.controller.ts), so this tab stays visible at MERCHANTS_VIEWER
// and `LedgerTab` itself only renders the merchant/project/gas-spend
// columns when the caller also holds FINANCE_VIEWER — same "omit, don't
// disable" rule as everywhere else on this page, just applied to columns
// instead of a whole tab. `invoices` is INVOICES_VIEWER — verified against
// the controllers' actual `@Roles(...)` decorators, not assumed from the
// plan text. Every other tab is MERCHANTS_VIEWER, same role as the page
// itself.
const TAB_CONFIGS: TabConfig[] = [
  { key: "overview", label: "Overview", role: AdminRole.MERCHANTS_VIEWER },
  { key: "invoices", label: "Invoices", role: AdminRole.INVOICES_VIEWER },
  { key: "accounts", label: "Accounts", role: AdminRole.MERCHANTS_VIEWER },
  // `GET /v1/withdrawals` is FINANCE_VIEWER OR CONFIG_MANAGER (see
  // withdrawals.ts's own comment) — neither role is MERCHANTS_VIEWER, so
  // this tab needs its own two-role gate rather than reusing the page's.
  { key: "withdrawals", label: "Withdrawals", role: AdminRole.FINANCE_VIEWER, altRole: AdminRole.CONFIG_MANAGER },
  { key: "assets", label: "Assets", role: AdminRole.MERCHANTS_VIEWER },
  { key: "transient-wallets", label: "Transient Wallets", role: AdminRole.MERCHANTS_VIEWER },
  { key: "policies", label: "Invoice Policy", role: AdminRole.MERCHANTS_VIEWER },
  { key: "api-keys", label: "API & Webhook", role: AdminRole.MERCHANTS_VIEWER },
  { key: "activity", label: "Activity", role: AdminRole.MERCHANTS_VIEWER },
];

function TabNav({
                  tabs,
                  active,
                  onChange,
                }: {
  tabs: TabConfig[];
  active: TabKey;
  onChange: (key: TabKey) => void;
}) {
  return (
    <div className="inline-flex flex-wrap items-center gap-0.5 rounded-full border border-border p-0.5">
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            aria-pressed={isActive}
            className={cn(
              "rounded-full px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer",
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Overview tab (MERCHANTS_VIEWER) ────────────────────────────────────────

function EditProfileDialog({
                             merchant,
                             merchantId,
                             onSaved,
                           }: {
  merchant: MerchantResponse;
  merchantId: string;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [businessName, setBusinessName] = useState(merchant.businessName);
  const [email, setEmail] = useState(merchant.email);
  const [error, setError] = useState<string | null>(null);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      // Reset from the latest merchant data every time the dialog opens.
      setBusinessName(merchant.businessName);
      setEmail(merchant.email);
      setError(null);
    }
  };

  const mutation = useMutation({
    mutationFn: () => {
      const patch: { businessName?: string; email?: string } = {};
      if (businessName.trim() && businessName.trim() !== merchant.businessName) {
        patch.businessName = businessName.trim();
      }
      if (email.trim() && email.trim() !== merchant.email) {
        patch.email = email.trim();
      }
      return updateMerchant(merchantId, patch);
    },
    onSuccess: () => {
      toast.success("Profile updated.");
      onSaved();
      setOpen(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to update profile.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!businessName.trim() || !email.trim()) {
      setError("Both fields are required.");
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Edit profile
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit profile</DialogTitle>
          <DialogDescription>Updates this merchant&apos;s business name and/or email.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-businessName">Business name</Label>
            <Input
              id="edit-businessName"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              autoComplete="organization"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-email">Email address</Label>
            <Input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} className="w-full sm:w-auto">
              {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin"/> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Reachable from the Tariff section header (`SectionCard`'s `action` slot,
// same placement convention as Profile's `EditProfileDialog` above) rather
// than the previous always-visible inline <select> — assigning a tariff is
// a deliberate action, not a value you'd want to change by an accidental
// click, so it goes behind an explicit "Assign tariff" / "Save" step like
// every other mutation on this page.

function AssignTariffDialog({
                              merchantId,
                              currentTariffId,
                              tariffOptions,
                              onSaved,
                            }: {
  merchantId: string;
  currentTariffId: string | null;
  tariffOptions: MerchantTariffOptionItem[];
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [tariffId, setTariffId] = useState(currentTariffId ?? "");

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) setTariffId(currentTariffId ?? "");
  };

  const mutation = useMutation({
    mutationFn: () => updateMerchant(merchantId, { tariffId: tariffId || null }),
    onSuccess: () => {
      toast.success("Tariff updated.");
      onSaved();
      setOpen(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to update tariff.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Assign tariff
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign tariff</DialogTitle>
          <DialogDescription>Choose the fee tariff applied to this merchant&apos;s invoices.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="assign-tariff-select">Tariff</Label>
            <select
              id="assign-tariff-select"
              className={cn(SELECT_CLASSNAME, "w-full")}
              value={tariffId}
              onChange={(e) => setTariffId(e.target.value)}
            >
              <option value="">— No tariff (fee-free) —</option>
              {tariffOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} className="w-full sm:w-auto">
              {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin"/> : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Task 14 (feature-031 Phase B): channel limit edit dialog — same
// header-action placement/dialog shape as AssignTariffDialog above, applied
// to a plain nullable integer instead of a select. Three distinct states a
// caller can choose between (not just "set a number"): "use the platform
// default" (`null`), "disabled entirely" (`0`, per Task 7's backend logic —
// deliberately NOT the same as null and must never be conflated with it in
// this UI), or a specific override (any other non-negative integer). A
// radio-style toggle between "Default" and "Custom" keeps those three states
// unambiguous, rather than an empty-string-means-null text input that could
// too easily be misread as "0".

function EditChannelLimitDialog({
  merchantId,
  currentLimit,
  onSaved,
}: {
  merchantId: string;
  currentLimit: number | null;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [useDefault, setUseDefault] = useState(currentLimit === null);
  const [customValue, setCustomValue] = useState(currentLimit !== null ? String(currentLimit) : "0");
  const [error, setError] = useState<string | null>(null);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setUseDefault(currentLimit === null);
      setCustomValue(currentLimit !== null ? String(currentLimit) : "0");
      setError(null);
    }
  };

  const mutation = useMutation({
    mutationFn: () => updateMerchant(merchantId, { channelLimit: useDefault ? null : Number(customValue) }),
    onSuccess: () => {
      toast.success("Channel limit updated.");
      onSaved();
      setOpen(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to update channel limit.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!useDefault && (!/^\d+$/.test(customValue.trim()) || Number(customValue) < 0)) {
      setError("Enter a whole number of 0 or more.");
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Edit limit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit channel limit</DialogTitle>
          <DialogDescription>
            The maximum number of channels this merchant may create (archived channels count too). A
            limit of 0 disables channel creation for this merchant entirely.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="channel-limit-mode"
                checked={useDefault}
                onChange={() => setUseDefault(true)}
              />
              Use the platform default
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="channel-limit-mode"
                checked={!useDefault}
                onChange={() => setUseDefault(false)}
              />
              Custom limit
            </label>
            {!useDefault && (
              <Input
                type="number"
                min={0}
                step={1}
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                className="max-w-[10rem]"
              />
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} className="w-full sm:w-auto">
              {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OverviewTab({
                       merchant,
                       merchantId,
                       roles,
                     }: {
  merchant: MerchantResponse;
  merchantId: string;
  roles: AdminRole[];
}) {
  const queryClient = useQueryClient();
  const canManage = hasRole(roles, AdminRole.MERCHANTS_MANAGER);

  const invalidateMerchant = () => {
    queryClient.invalidateQueries({ queryKey: ["merchant", merchantId] });
    queryClient.invalidateQueries({ queryKey: ["merchants"] });
  };

  const accessMutation = useMutation({
    mutationFn: (accessGranted: boolean) => updateMerchant(merchantId, { accessGranted }),
    onSuccess: (data) => {
      queryClient.setQueryData(["merchant", merchantId], data);
      queryClient.invalidateQueries({ queryKey: ["merchants"] });
      toast.success(data.accessGranted ? "Access granted." : "Access revoked.");
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to update access.");
    },
  });

  const demoMutation = useMutation({
    mutationFn: (isDemo: boolean) => updateMerchant(merchantId, { isDemo }),
    onSuccess: (data) => {
      queryClient.setQueryData(["merchant", merchantId], data);
      queryClient.invalidateQueries({ queryKey: ["merchants"] });
      toast.success(data.isDemo ? "Marked as demo merchant." : "Demo restrictions removed.");
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to update demo mode.");
    },
  });

  const {
    data: tariff,
    isPending: tariffPending,
    isError: tariffError,
  } = useQuery({
    queryKey: ["merchant", merchantId, "tariff"],
    queryFn: () => getMerchantTariff(merchantId),
  });

  const { data: tariffOptions } = useQuery({
    queryKey: ["merchant-tariff-options"],
    queryFn: getMerchantTariffOptions,
    enabled: canManage,
  });

  const invalidateTariff = () => {
    queryClient.invalidateQueries({ queryKey: ["merchant", merchantId] });
    queryClient.invalidateQueries({ queryKey: ["merchant", merchantId, "tariff"] });
  };

  const {
    data: stats,
    isPending: statsPending,
    isError: statsError,
  } = useQuery({
    queryKey: ["merchant", merchantId, "stats"],
    queryFn: () => getMerchantStats(merchantId),
  });

  return (
    <div className="space-y-6">
      <SectionCard title="Quick stats" description="Completed volume, fees, and balances across all assets.">
        {statsPending ? (
          <BlockStatus label="Loading stats…"/>
        ) : statsError || !stats ? (
          <BlockStatus label="Failed to load stats."/>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard label="Completed volume" value={formatUsdAmount(stats.completedVolumeUsd)}/>
              <StatCard label="Fees" value={formatUsdAmount(stats.feesUsd)}/>
              <StatCard label="Balance total" value={formatUsdAmount(stats.balanceTotalUsd)}/>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.values(InvoiceStatus).map((status) => (
                <StatusBadge
                  key={status}
                  label={`${status}: ${stats.invoiceCountsByStatus[status]}`}
                  color={INVOICE_STATUS_COLOR[status]}
                />
              ))}
            </div>
          </>
        )}
      </SectionCard>

      <SectionCard
        title="Profile"
        description="Business identity and account status."
        action={
          canManage && (
            <EditProfileDialog merchant={merchant} merchantId={merchantId} onSaved={invalidateMerchant}/>
          )
        }
      >
        <dl className="space-y-2 text-sm">
          <DetailRow label="Business name" value={merchant.businessName}/>
          <DetailRow label="Email" value={merchant.email}/>
          <DetailRow
            label="Status"
            value={
              <StatusBadge
                label={formatStatusLabel(merchant.status)}
                color={MERCHANT_STATUS_COLOR[merchant.status]}
              />
            }
          />
          <DetailRow label="Created" value={formatTimestamp(merchant.createdAt)}/>
        </dl>
      </SectionCard>

      <SectionCard title="Access" description="Portal sign-in access for this merchant.">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <StatusBadge
            label={merchant.accessGranted ? "Access granted" : "Access revoked"}
            color={merchant.accessGranted ? "success" : "danger"}
          />
          {canManage &&
            (merchant.accessGranted ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" disabled={accessMutation.isPending}>
                    {accessMutation.isPending ? "Revoking…" : "Revoke access"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Revoke this merchant&apos;s access?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {merchant.businessName} will immediately lose the ability to sign in and use the
                      merchant portal. You can grant access back at any time.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => accessMutation.mutate(false)}>
                      Revoke access
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button size="sm" onClick={() => accessMutation.mutate(true)} disabled={accessMutation.isPending}>
                {accessMutation.isPending ? "Granting…" : "Grant access"}
              </Button>
            ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Demo merchant"
        description="A demo merchant's portal is read-only: nothing can be changed and no funds can leave."
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <StatusBadge
            label={merchant.isDemo ? "Demo — read-only" : "Not a demo merchant"}
            color={merchant.isDemo ? "purple" : "neutral"}
          />
          {canManage &&
            (merchant.isDemo ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" disabled={demoMutation.isPending}>
                    {demoMutation.isPending ? "Removing…" : "Remove demo restrictions"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove demo restrictions?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Anyone signed in as {merchant.businessName} will immediately be able to withdraw, refund,
                      and change API keys, webhooks and payment rules. Mark it as demo again as soon as your
                      change is done.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => demoMutation.mutate(false)}>
                      Remove restrictions
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button size="sm" onClick={() => demoMutation.mutate(true)} disabled={demoMutation.isPending}>
                {demoMutation.isPending ? "Marking…" : "Mark as demo merchant"}
              </Button>
            ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Tariff"
        description="Fee tariff assigned to this merchant."
        action={
          canManage && (
            <AssignTariffDialog
              merchantId={merchantId}
              currentTariffId={merchant.tariffId}
              tariffOptions={tariffOptions ?? []}
              onSaved={invalidateTariff}
            />
          )
        }
      >
        {tariffPending ? (
          <BlockStatus label="Loading tariff…"/>
        ) : tariffError ? (
          <BlockStatus label="Failed to load tariff."/>
        ) : (
          <div className="space-y-4">
            {tariff ? (
              <>
                <div className="font-medium">{tariff.name}</div>
                {tariff.rules.length === 0 ? (
                  <BlockStatus label="No per-asset rules configured."/>
                ) : (
                  <div className="overflow-hidden rounded-lg border">
                    <table className="w-full text-sm">
                      <thead>
                      <tr className="bg-muted text-left text-xs font-semibold text-muted-foreground">
                        <th className="px-4 py-2.5 font-semibold">Asset</th>
                        <th className="px-4 py-2.5 font-semibold">Network</th>
                        <th className="px-4 py-2.5 font-semibold">Percent</th>
                        <th className="px-4 py-2.5 font-semibold">Fixed</th>
                        <th className="px-4 py-2.5 font-semibold">Min</th>
                        <th className="px-4 py-2.5 font-semibold">Max</th>
                      </tr>
                      </thead>
                      <tbody>
                      {tariff.rules.map((rule) => (
                        <tr key={rule.assetId} className="border-t">
                          <td className="px-4 py-2 font-medium">{rule.assetSymbol}</td>
                          <td className="px-4 py-2">
                            <StatusBadge label={formatStatusLabel(rule.assetNetwork)} color="neutral"/>
                          </td>
                          <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                            {rule.percentBps / 100}%
                          </td>
                          <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                            {formatUsdAmount(rule.fixedUsd)}
                          </td>
                          <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                            {rule.minUsd !== null ? formatUsdAmount(rule.minUsd) : "—"}
                          </td>
                          <td className="px-4 py-2" style={{ fontVariantNumeric: "tabular-nums" }}>
                            {rule.maxUsd !== null ? formatUsdAmount(rule.maxUsd) : "—"}
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <BlockStatus label="Fee-free — no tariff assigned."/>
            )}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Channel limit"
        description="Maximum number of payment channels this merchant may create (feature-031)."
        action={
          canManage && (
            <EditChannelLimitDialog
              merchantId={merchantId}
              currentLimit={merchant.channelLimit}
              onSaved={invalidateMerchant}
            />
          )
        }
      >
        <dl className="space-y-2 text-sm">
          <DetailRow
            label="Limit"
            value={
              merchant.channelLimit === null
                ? "Platform default"
                : merchant.channelLimit === 0
                  ? "0 — channels disabled"
                  : merchant.channelLimit
            }
          />
        </dl>
      </SectionCard>
    </div>
  );
}

// ─── Deposit wallets tab (MERCHANTS_VIEWER) ─────────────────────────────────
//
// Renders the backend's `TransientWallet` resource — "transient" is its
// internal lifecycle name (created per-deposit, swept once settled), but
// "Deposit wallets" is what an admin actually recognizes it as, so that's
// the label/copy this tab uses everywhere. The `key`, API functions, and
// `TransientWalletStatus` type stay as-is — renaming those would ripple into
// the URL's `?tab=` param and the backend contract for no real benefit.
//
// Genuinely server-paginated (unlike the merchants list), so this uses the
// real DataTable with local `useState` for page/pageSize/status — same "tab,
// not a route, no URL persistence needed" reasoning as the merchants list
// page's own deviation from `useTableState`.

interface DepositWalletsState {
  page: number;
  pageSize: number;
  status: TransientWalletStatus | "";
}

function DepositWalletsTab({ merchantId }: { merchantId: string }) {
  const [state, setState] = useState<DepositWalletsState>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    status: "",
  });

  const { data, isPending, isError } = useQuery({
    queryKey: ["merchant", merchantId, "transient-wallets", state.page, state.pageSize, state.status],
    queryFn: () =>
      getMerchantTransientWallets(merchantId, {
        page: state.page,
        pageSize: state.pageSize,
        status: state.status || undefined,
      }),
  });

  const columns: DataTableColumn<MerchantTransientWalletItem>[] = [
    { key: "asset", header: "Asset", cell: (w) => w.assetSymbol },
    {
      key: "network",
      header: "Network",
      cell: (w) => <StatusBadge label={formatStatusLabel(w.assetNetwork)} color="neutral"/>,
    },
    {
      key: "address",
      header: "Address",
      cell: (w) => <AddressWithVault address={w.address} vaultId={w.fireblocksVaultId} network={w.assetNetwork}/>,
    },
    {
      key: "status",
      header: "Status",
      filter: columnFilter(
        {
          filters: { status: state.status || undefined },
          setFilters: (patch: { status?: TransientWalletStatus }) =>
            setState((s) => ({ ...s, status: patch.status ?? "", page: 1 })),
        },
        ["status"],
        (draft, set) => (
          <SelectFilterField
            id="filter-wallet-status"
            label="Status"
            allLabel="All statuses"
            options={enumOptions(Object.values(TransientWalletStatus))}
            value={draft.status}
            onChange={(v) => set("status", v as TransientWalletStatus | undefined)}
          />
        ),
      ),
      cell: (w) => (
        <StatusBadge label={formatStatusLabel(w.status)} color={TRANSIENT_WALLET_STATUS_COLOR[w.status]}/>
      ),
    },
    {
      key: "total",
      header: "Token balance",
      cell: (w) => (
        <div className="flex items-center gap-1">
          <Amount native={w.balanceTotal} symbol={w.assetSymbol} usd={w.balanceTotalUsd}/>
          <BalanceBreakdownDialog account={w}/>
        </div>
      ),
    },
    {
      // `w.assetSymbol` is this wallet's own (possibly token) asset, not
      // necessarily the native gas-coin `balanceNative` is denominated in
      // (e.g. a USDT-on-Ethereum wallet's balanceNative is an ETH amount) —
      // use the backend-resolved `nativeAssetSymbol` instead, falling back to
      // a plain trimmed decimal on the rare row where it's null (no asset
      // flagged native for that network yet).
      key: "nativeBalance",
      header: "Gas balance",
      cell: (w) =>
        w.nativeAssetSymbol ? (
          <Amount native={w.balanceNative} symbol={w.nativeAssetSymbol} usd={w.balanceNativeUsd}/>
        ) : (
          trimDecimalZeros(w.balanceNative)
        ),
    },
    {
      key: "updated",
      header: "Updated",
      cell: (w) => (w.balanceUpdatedAt ? formatTimestamp(w.balanceUpdatedAt) : "—"),
    },
  ];

  return (
    <DataTable
      searchable={false}
      columns={columns}
      items={data?.data ?? []}
      total={data?.total ?? 0}
      page={state.page}
      pageSize={state.pageSize}
      loading={isPending}
      error={isError ? "Failed to load deposit wallets." : null}
      onPageChange={(page) => setState((s) => ({ ...s, page }))}
      onPageSizeChange={(pageSize) => setState((s) => ({ ...s, pageSize, page: 1 }))}
      getRowKey={(w) => w.id}
      emptyMessage="No transient wallets."
    />
  );
}

// ─── Ledger tab (MERCHANTS_VIEWER; gas spend/project fees need FINANCE_VIEWER) ──
//
// Merges the former Accounts and Ledgers tabs into one row-per-account
// table, joined by `accountId`. Balance columns (Available/Pending/Frozen/
// Total) mirror the merchant portal's own Accounts page exactly — same
// crypto-native + USD stacked cells, Total = Available + Pending + Frozen
// (exact decimal addition, `balanceLocked` excluded — the merchant portal
// has no equivalent bucket). Gas spend/Project fees (the platform's accrued,
// uncollected fee balance for the account) stay FINANCE_VIEWER-only (see the
// TAB_CONFIGS comment for why the tab itself is MERCHANTS_VIEWER): the
// columns are simply omitted, not disabled, for a caller who lacks that
// role.
//
// Three color-coded groups (a spanning group-header row above the column
// labels, plus a matching light tint carried down every cell in that group)
// give the merchant-side vs. platform-side money a visual split at a glance:
// merchant balances (`info` blue), project/platform balances (`purple`),
// and the Total balance = merchant Total + Project fees grand total
// (`success` green) — same fixed `StatusColor` hex palette `StatusBadge`/
// `StatCard` already draw from, just applied as a background tint
// (`${hex}1A` for the header band, a lighter `${hex}0D` down through the
// body cells) rather than a dot/pill.

const LEDGER_MERCHANT_COLOR = STATUS_DOT_COLOR.info;
const LEDGER_PROJECT_COLOR = STATUS_DOT_COLOR.purple;
const LEDGER_TOTAL_COLOR = STATUS_DOT_COLOR.success;

const ledgerMerchantCellStyle = { backgroundColor: `${LEDGER_MERCHANT_COLOR}0D` };
const ledgerProjectCellStyle = { backgroundColor: `${LEDGER_PROJECT_COLOR}0D` };
const ledgerTotalCellStyle = { backgroundColor: `${LEDGER_TOTAL_COLOR}0D` };

// Column-header band for the Merchant Ledger group (Available/Processing/
// Locked) — stronger alpha than `ledgerMerchantCellStyle`'s body tint, same
// two-tier depth convention `_app.accounts.tsx`'s `columnTint()` uses for the
// equivalent columns on the global Accounts page (this tab was previously
// reusing the lighter body tint for its column headers too, flatter than
// that page's).
const ledgerMerchantHeaderStyle = { backgroundColor: `${LEDGER_MERCHANT_COLOR}1A` };

function LedgerTab({ merchantId, roles }: { merchantId: string; roles: AdminRole[] }) {
  const canViewFinance = hasRole(roles, AdminRole.FINANCE_VIEWER);
  const [drillDown, setDrillDown] = useState<LedgerDrillDownTarget | null>(null);

  const {
    data: accounts,
    isPending: accountsPending,
    isError: accountsError,
  } = useQuery({
    queryKey: ["merchant", merchantId, "accounts"],
    queryFn: () => getMerchantAccounts(merchantId),
  });

  const {
    data: ledgers,
    isPending: ledgersPending,
    isError: ledgersError,
  } = useQuery({
    queryKey: ["merchant", merchantId, "ledgers"],
    queryFn: () => getMerchantLedgers(merchantId),
    enabled: canViewFinance,
  });

  const ledgerByAccountId = useMemo(
    () => new Map((ledgers ?? []).map((ledger) => [ledger.accountId, ledger])),
    [ledgers],
  );

  if (accountsPending || (canViewFinance && ledgersPending)) {
    return <BlockStatus label="Loading ledger…"/>;
  }
  if (accountsError || !accounts || (canViewFinance && ledgersError)) {
    return <BlockStatus label="Failed to load ledger."/>;
  }
  if (accounts.length === 0) return <BlockStatus label="No accounts yet."/>;

  return (
    <>
      <div className="overflow-hidden overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
          <tr className="text-left text-xs font-semibold text-muted-foreground">
            <th className="bg-muted px-4 py-1.5" colSpan={3}/>
            <th
              className="px-4 py-1.5 text-center"
              colSpan={3}
              style={{ backgroundColor: `${LEDGER_MERCHANT_COLOR}1A`, color: LEDGER_MERCHANT_COLOR }}
            >
              Merchant Ledger
            </th>
            {canViewFinance && (
              <>
                <th
                  className="px-4 py-1.5 text-center"
                  colSpan={2}
                  style={{ backgroundColor: `${LEDGER_PROJECT_COLOR}1A`, color: LEDGER_PROJECT_COLOR }}
                >
                  Project Ledger
                </th>
                <th
                  className="px-4 py-1.5 text-center"
                  style={{ backgroundColor: `${LEDGER_TOTAL_COLOR}1A`, color: LEDGER_TOTAL_COLOR }}
                >
                  Wallet
                </th>
              </>
            )}
          </tr>
          <tr className="bg-muted text-left text-xs font-semibold text-muted-foreground">
            <th className="px-4 py-2.5 font-semibold">Asset</th>
            <th className="px-4 py-2.5 font-semibold">Address</th>
            <th className="px-4 py-2.5 font-semibold">Status</th>
            <th className="px-4 py-2.5 font-semibold" style={ledgerMerchantHeaderStyle}>Available</th>
            <th className="px-4 py-2.5 font-semibold" style={ledgerMerchantHeaderStyle}>Processing</th>
            <th className="px-4 py-2.5 font-semibold" style={ledgerMerchantHeaderStyle}>Locked</th>
            {canViewFinance && (
              <>
                <th className="px-4 py-2.5 font-semibold" style={ledgerProjectCellStyle}>Gas spend</th>
                <th className="px-4 py-2.5 font-semibold" style={ledgerProjectCellStyle}>Project fees</th>
                <th className="px-4 py-2.5 font-semibold" style={ledgerTotalCellStyle}>Total Balance</th>
              </>
            )}
          </tr>
          </thead>
          <tbody>
          {accounts.map((account) => {
            const ledger = ledgerByAccountId.get(account.id);
            const assetLabel = `${account.assetSymbol} · ${formatStatusLabel(account.assetNetwork)}`;
            // Clicking anywhere on the row (except the address cell, which
            // has its own copy-button affordance — see its `onClick`
            // stopPropagation below) opens the drill-down dialog. Only
            // reachable once both ledgers exist for the account.
            const openHistory = () =>
              ledger &&
              setDrillDown({
                type: "merchant",
                ledgerId: ledger.merchantLedgerId,
                otherLedgerId: ledger.projectLedgerId,
                assetSymbol: ledger.assetSymbol,
                subtitle: assetLabel,
              });
            return (
              <tr
                key={account.id}
                className={cn("border-t", ledger && "cursor-pointer hover:bg-muted/50")}
                onClick={openHistory}
              >
                <td className="px-4 py-2">
                  <AssetLabel symbol={account.assetSymbol} network={account.assetNetwork}/>
                </td>
                <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                  <AddressWithVault address={account.address} vaultId={account.fireblocksVaultId} network={account.assetNetwork}/>
                </td>
                <td className="px-4 py-2">
                  <StatusBadge label={account.status} color={ACCOUNT_STATUS_COLOR[account.status]}/>
                </td>
                <td className="px-4 py-2" style={ledgerMerchantCellStyle}>
                  <span className="block text-sm text-foreground">
                    <Amount native={account.balanceTotal} symbol={account.assetSymbol} usd={account.balanceTotalUsd}/>
                  </span>
                </td>
                <td className="px-4 py-2" style={ledgerMerchantCellStyle}>
                  <span className="block text-sm text-foreground">
                    <Amount native={account.balancePending} symbol={account.assetSymbol} usd={account.balancePendingUsd}/>
                  </span>
                </td>
                <td className="px-4 py-2" style={ledgerMerchantCellStyle}>
                  <span className="block text-sm text-foreground">
                    <Amount native={account.balanceFrozen} symbol={account.assetSymbol} usd={account.balanceFrozenUsd}/>
                  </span>
                </td>
                {canViewFinance && (
                  <>
                    <td className="px-4 py-2" style={ledgerProjectCellStyle}>
                      {ledger ? (
                        <span className="block text-sm text-foreground">
                          <Amount
                            native={ledger.projectLedgerGasCostNative}
                            symbol={ledger.assetSymbol}
                            usd={ledger.projectLedgerGasCostUsd}
                          />
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-2" style={ledgerProjectCellStyle}>
                      {ledger ? (
                        <span className="block text-sm text-foreground">
                          <Amount
                            native={ledger.projectLedgerBalanceNative}
                            symbol={ledger.assetSymbol}
                            usd={ledger.projectLedgerBalanceUsd}
                          />
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-2" style={ledgerTotalCellStyle}>
                      {ledger ? (
                        <span className="block text-sm text-foreground">
                          <Amount
                            native={ledger.totalBalanceNative}
                            symbol={account.assetSymbol}
                            usd={ledger.totalBalanceUsd}
                          />
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>

      {canViewFinance && (
        <LedgerTransactionsDialog
          target={drillDown}
          onOpenChange={(open) => {
            if (!open) setDrillDown(null);
          }}
        />
      )}
    </>
  );
}

// ─── Invoices tab (INVOICES_VIEWER) ──────────────────────────────────────────
//
// Self-contained: `_app.invoices.tsx` hasn't been upgraded to the shared
// table kit yet (Task 26) — this tab reuses `listInvoices({ merchantId, ...
// })` directly with local pagination state, rendered via DataTable. No row
// interaction (no invoice detail page exists yet, also Task 26).

interface InvoicesState {
  page: number;
  pageSize: number;
}

function InvoicesTab({ merchantId, roles }: { merchantId: string; roles: AdminRole[] }) {
  const canView = hasRole(roles, AdminRole.INVOICES_VIEWER);
  const [state, setState] = useState<InvoicesState>({ page: 1, pageSize: DEFAULT_PAGE_SIZE });

  const { data, isPending, isError } = useQuery({
    queryKey: ["merchant", merchantId, "invoices", state.page, state.pageSize],
    queryFn: () => listInvoices({ merchantId, page: state.page, pageSize: state.pageSize }),
    enabled: canView,
  });

  if (!canView) return null;

  const columns: DataTableColumn<InvoiceListItem>[] = [
    {
      key: "publicId",
      header: "Invoice ID",
      cell: (i) => (
        <Link to="/invoices/$invoiceId" params={{ invoiceId: i.id }} className="group -m-2 block p-2">
          <span className="group-hover:underline">{i.publicId}</span>
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      // subStatus, when present, only ever pairs with ACTION_REQUIRED and
      // already fully explains the state — same convention as the global
      // invoices list (`_app.invoices.tsx`).
      cell: (i) => (
        <StatusBadge label={formatStatusLabel(i.subStatus ?? i.status)} color={INVOICE_STATUS_COLOR[i.status]}/>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      cell: (i) => <AssetLabel symbol={i.asset.symbol} network={i.asset.network}/>,
    },
    {
      key: "fiatAmount",
      header: "Paid",
      cell: (i) => (
        <CryptoFiatAmount
          native={i.cryptoAmount}
          symbol={i.asset.symbol}
          fiatAmount={i.fiatAmount}
          fiatCurrency={i.fiatCurrency}
        />
      ),
    },
    {
      key: "fee",
      header: "Fee",
      cell: (i) => (
        <span className="block text-sm text-foreground">
          <Amount native={i.cryptoFee} symbol={i.asset.symbol} usd={i.feeUsd}/>
        </span>
      ),
    },
    {
      key: "merchantAmount",
      header: "Settlement",
      cell: (i) => (
        <span className="block text-sm text-foreground">
          <Amount native={i.merchantAmount} symbol={i.asset.symbol} usd={i.merchantAmountUsd}/>
        </span>
      ),
    },
    {
      key: "txHash",
      header: "Tx hash",
      cell: (i) => <HashLink value={i.txHash} type="tx" network={i.asset.network} className="text-xs" />,
    },
    { key: "createdAt", header: "Created", cell: (i) => formatTimestamp(i.createdAt) },
  ];

  return (
    <DataTable
      searchable={false}
      columns={columns}
      items={data?.data ?? []}
      total={data?.total ?? 0}
      page={state.page}
      pageSize={state.pageSize}
      loading={isPending}
      error={isError ? "Failed to load invoices." : null}
      onPageChange={(page) => setState((s) => ({ ...s, page }))}
      onPageSizeChange={(pageSize) => setState((s) => ({ ...s, pageSize, page: 1 }))}
      getRowKey={(i) => i.id}
      emptyMessage="No invoices for this merchant."
    />
  );
}

// ─── Withdrawals tab (FINANCE_VIEWER OR CONFIG_MANAGER) ─────────────────────
//
// Reuses the global withdrawals queue's own `listWithdrawals`/columns, merely
// scoped with `merchantId` (already a supported filter on both the backend
// query and `ListWithdrawalsParams`) — same "self-contained, local pagination
// state, plain DataTable" shape as `InvoicesTab` above. No detail dialog: a
// withdrawal's full review UI (approve/reject, limit breaches) lives on the
// global /withdrawals page, out of this read-only tab's scope.

interface WithdrawalsState {
  page: number;
  pageSize: number;
}

function WithdrawalsTab({ merchantId }: { merchantId: string }) {
  const [state, setState] = useState<WithdrawalsState>({ page: 1, pageSize: DEFAULT_PAGE_SIZE });

  const { data, isPending, isError } = useQuery({
    queryKey: ["merchant", merchantId, "withdrawals", state.page, state.pageSize],
    queryFn: () => listWithdrawals({ merchantId, page: state.page, pageSize: state.pageSize }),
  });

  const columns: DataTableColumn<WithdrawalListItem>[] = [
    {
      key: "asset",
      header: "Asset",
      cell: (w) => <AssetLabel symbol={w.assetSymbol} network={w.assetNetwork} />,
    },
    {
      key: "amount",
      header: "Amount",
      className: "text-right",
      cell: (w) => (
        <span className="block text-sm text-foreground">
          <Amount native={w.amount.native} symbol={w.assetSymbol} usd={w.amount.usd} />
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (w) => (
        <div className="flex flex-wrap items-center gap-1">
          <StatusBadge label={formatStatusLabel(w.status)} color={WITHDRAWAL_STATUS_COLOR[w.status]} />
          {w.limitBreaches.length > 0 && <StatusBadge label="Limit" color="warning" />}
        </div>
      ),
    },
    {
      key: "address",
      header: "Address",
      cell: (w) => <HashLink value={w.address} type="address" network={w.assetNetwork} />,
    },
    {
      key: "payoutTxHash",
      header: "Payout tx",
      cell: (w) =>
        w.payoutTxHash ? (
          <HashLink value={w.payoutTxHash} type="tx" network={w.assetNetwork} className="text-xs" />
        ) : (
          "—"
        ),
    },
    { key: "createdAt", header: "Created", cell: (w) => formatTimestamp(w.createdAt) },
  ];

  return (
    <DataTable
      searchable={false}
      columns={columns}
      items={data?.data ?? []}
      total={data?.total ?? 0}
      page={state.page}
      pageSize={state.pageSize}
      loading={isPending}
      error={isError ? "Failed to load withdrawals." : null}
      onPageChange={(page) => setState((s) => ({ ...s, page }))}
      onPageSizeChange={(pageSize) => setState((s) => ({ ...s, pageSize, page: 1 }))}
      getRowKey={(w) => w.id}
      emptyMessage="No withdrawals for this merchant."
    />
  );
}

// ─── Assets tab (MERCHANTS_VIEWER, + MERCHANTS_MANAGER toggle) ──────────────

function AssetsTab({ merchantId, roles }: { merchantId: string; roles: AdminRole[] }) {
  const queryClient = useQueryClient();
  const canManage = hasRole(roles, AdminRole.MERCHANTS_MANAGER);

  const { data, isPending, isError } = useQuery({
    queryKey: ["merchant", merchantId, "assets"],
    queryFn: () => getMerchantAssets(merchantId),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ assetId, disabled }: { assetId: string; disabled: boolean }) =>
      setMerchantAssetDisabled(merchantId, assetId, disabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["merchant", merchantId, "assets"] });
      toast.success("Asset updated.");
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Failed to update asset.");
    },
  });

  if (isPending) return <BlockStatus label="Loading assets…"/>;
  if (isError || !data) return <BlockStatus label="Failed to load assets."/>;
  if (data.length === 0) return <BlockStatus label="No enabled assets."/>;

  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-sm">
        <thead>
        <tr className="bg-muted text-left text-xs font-semibold text-muted-foreground">
          <th className="px-4 py-2.5 font-semibold">Asset</th>
          <th className="px-4 py-2.5 font-semibold">Status</th>
          <th className="px-4 py-2.5 font-semibold">Rate</th>
          <th className="px-4 py-2.5 font-semibold">Effective fee</th>
          {canManage && <th className="px-4 py-2.5 font-semibold">Action</th>}
        </tr>
        </thead>
        <tbody>
        {data.map((asset) => (
          <tr key={asset.assetId} className="border-t">
            <td className="px-4 py-2">
              <AssetLabel symbol={asset.symbol} network={asset.network}/>
            </td>
            <td className="px-4 py-2">
              <StatusBadge
                label={asset.disabled ? "Disabled" : "Enabled"}
                color={asset.disabled ? "neutral" : "success"}
              />
            </td>
            <td className="px-4 py-2">
              {asset.rate ? (
                <div>
                  <div>{formatUsdAmount(asset.rate.usdRate)}</div>
                  <div className="text-xs text-muted-foreground">as of {formatTimestamp(asset.rate.fetchedAt)}</div>
                </div>
              ) : (
                <span className="text-muted-foreground">No rate available</span>
              )}
            </td>
            <td className="px-4 py-2">
              {asset.fee ? (
                (() => {
                  const { percentBps, fixedUsd, minUsd, maxUsd } = asset.fee;
                  const minMaxParts: string[] = [];
                  if (minUsd !== null) minMaxParts.push(`min ${formatUsdAmount(minUsd)}`);
                  if (maxUsd !== null) minMaxParts.push(`max ${formatUsdAmount(maxUsd)}`);
                  return (
                    <div>
                      <div>
                        {(percentBps / 100).toFixed(2)}% + {formatUsdAmount(fixedUsd)}
                      </div>
                      {minMaxParts.length > 0 && (
                        <div className="text-xs text-muted-foreground">{minMaxParts.join(" / ")}</div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <span className="text-muted-foreground">No fee</span>
              )}
            </td>
            {canManage && (
              <td className="px-4 py-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={toggleMutation.isPending}
                  onClick={() => toggleMutation.mutate({ assetId: asset.assetId, disabled: !asset.disabled })}
                >
                  {asset.disabled ? "Enable" : "Disable"}
                </Button>
              </td>
            )}
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Invoice policies tab (MERCHANTS_VIEWER, read-only) ─────────────────────
//
// Mirrors the merchant portal's own Invoice Policy page
// (apps/merchant/src/routes/_app.invoice-policy.tsx) card-for-card — same 3
// categories, same action descriptions/icons, same threshold note — so an
// admin sees exactly what the merchant sees. Read-only, per this tab's own
// long-standing MERCHANTS_VIEWER scope: no click handlers, no mutation, no
// threshold input — changing the policy is the merchant's own call, made on
// their own portal.

const POLICY_CARD_META: Record<
  InvoiceSubStatus.OVERPAID | InvoiceSubStatus.UNDERPAID | InvoiceSubStatus.PAID_LATE,
  { icon: LucideIcon; tone: "amber" | "blue" | "slate"; title: string; desc: string }
> = {
  [InvoiceSubStatus.OVERPAID]: {
    icon: ArrowRightLeft,
    tone: "blue",
    title: "Overpayment handling",
    desc: "When a customer sends more than the invoice amount.",
  },
  [InvoiceSubStatus.UNDERPAID]: {
    icon: AlertTriangle,
    tone: "amber",
    title: "Underpayment handling",
    desc: "When a customer sends less than the invoice amount.",
  },
  [InvoiceSubStatus.PAID_LATE]: {
    icon: Clock,
    tone: "slate",
    title: "Late payment handling",
    desc: "When funds arrive after the invoice window has expired.",
  },
};

const POLICY_ACTION_LABELS: Record<InvoicePolicyAction, string> = {
  [InvoicePolicyAction.MANUAL]: "Manual review",
  [InvoicePolicyAction.AUTO_REFUND]: "Auto-refund",
  [InvoicePolicyAction.AUTO_COMPLETE]: "Auto-complete",
};

const POLICY_ACTION_OPTIONS: { action: InvoicePolicyAction; desc: string; icon: LucideIcon }[] = [
  {
    action: InvoicePolicyAction.MANUAL,
    desc: "Flagged for manual review — the merchant decides whether to accept or refund each one.",
    icon: Eye,
  },
  {
    action: InvoicePolicyAction.AUTO_REFUND,
    desc: "Automatically returned to the customer, minus network fees.",
    icon: RefreshCw,
  },
  {
    action: InvoicePolicyAction.AUTO_COMPLETE,
    desc: "Automatically accepted as-is and settled.",
    icon: CheckCircle2,
  },
];

const POLICY_TONE_CLASSNAME: Record<"amber" | "blue" | "slate", string> = {
  amber: "bg-amber-50 text-amber-700",
  blue: "bg-blue-50 text-blue-700",
  slate: "bg-slate-100 text-slate-700",
};

function PolicyOption({
  active,
  title,
  desc,
  icon: Icon,
}: {
  active: boolean;
  title: string;
  desc: string;
  icon: LucideIcon;
}) {
  return (
    <div
      className={cn(
        "flex w-full items-start gap-3 rounded-lg border p-3.5 text-left",
        active ? "border-primary bg-primary/[0.02] ring-1 ring-primary" : "border-border",
      )}
    >
      <div
        className={cn(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
          active ? "bg-primary text-primary-foreground" : "bg-accent text-muted-foreground",
        )}
      >
        <Icon className="h-4 w-4"/>
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
      </div>
      {active && (
        <div className="mt-1 h-4 w-4 shrink-0 rounded-full border-2 border-primary bg-primary">
          <div className="mx-auto mt-0.5 h-1.5 w-1.5 rounded-full bg-primary-foreground"/>
        </div>
      )}
    </div>
  );
}

function PolicyCard({ policy }: { policy: MerchantPolicyItem }) {
  const meta = POLICY_CARD_META[policy.subStatus as keyof typeof POLICY_CARD_META];
  const Icon = meta.icon;
  const showThreshold =
    policy.subStatus === InvoiceSubStatus.UNDERPAID &&
    policy.action === InvoicePolicyAction.AUTO_COMPLETE &&
    policy.maxShortfallPercent !== null;

  return (
    <section className="rounded-xl border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg", POLICY_TONE_CLASSNAME[meta.tone])}>
          <Icon className="h-5 w-5"/>
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-foreground">{meta.title}</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">{meta.desc}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-2.5 md:grid-cols-3">
        {POLICY_ACTION_OPTIONS.map((opt) => (
          <PolicyOption
            key={opt.action}
            active={policy.action === opt.action}
            title={POLICY_ACTION_LABELS[opt.action]}
            desc={opt.desc}
            icon={opt.icon}
          />
        ))}
      </div>

      {showThreshold && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg bg-muted px-4 py-3 text-sm text-foreground">
          <span>Only auto-completes if the shortfall is under</span>
          <span className="font-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
            {policy.maxShortfallPercent}%
          </span>
          <span>of the invoice. Larger shortfalls are flagged for manual review instead.</span>
        </div>
      )}
    </section>
  );
}

function PoliciesTab({ merchantId }: { merchantId: string }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["merchant", merchantId, "policies"],
    queryFn: () => getMerchantPolicies(merchantId),
  });

  if (isPending) return <BlockStatus label="Loading invoice policies…"/>;
  if (isError || !data) return <BlockStatus label="Failed to load invoice policies."/>;

  // HIGH_RISK is AML's queue, not a resolution policy the merchant
  // configures — the backend still returns a row for it (it's technically
  // policy-eligible), this page just doesn't surface it.
  const policies = data.filter((policy) => policy.subStatus !== InvoiceSubStatus.HIGH_RISK);
  if (policies.length === 0) return <BlockStatus label="No policies configured."/>;

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        How this merchant has configured overpayment, underpayment, and late-payment handling.
        Read-only — the merchant manages this from their own portal.
      </p>
      {policies.map((policy) => (
        <PolicyCard key={policy.subStatus} policy={policy}/>
      ))}
    </div>
  );
}

// ─── API keys tab (MERCHANTS_VIEWER) ────────────────────────────────────────
//
// Combines the former Webhook and API keys tabs into one "API access"
// layout — a merchant's webhook config, its delivery history, and its API
// keys are all facets of the same "how this merchant talks to us
// programmatically" concern, not three unrelated resources.

interface WebhookDeliveriesState {
  page: number;
  pageSize: number;
}

function ApiKeysTab({ merchantId }: { merchantId: string }) {
  const {
    data: webhook,
    isPending: webhookPending,
    isError: webhookError,
  } = useQuery({
    queryKey: ["merchant", merchantId, "webhook"],
    queryFn: () => getMerchantWebhook(merchantId),
  });

  const {
    data: apiKeys,
    isPending: apiKeysPending,
    isError: apiKeysError,
  } = useQuery({
    queryKey: ["merchant", merchantId, "api-keys"],
    queryFn: () => getMerchantApiKeys(merchantId),
  });

  const [state, setState] = useState<WebhookDeliveriesState>({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  const {
    data: deliveries,
    isPending: deliveriesPending,
    isError: deliveriesError,
  } = useQuery({
    queryKey: ["merchant", merchantId, "webhook-deliveries", state.page, state.pageSize],
    queryFn: () => getMerchantWebhookDeliveries(merchantId, { page: state.page, pageSize: state.pageSize }),
  });

  const deliveryColumns: DataTableColumn<WebhookDeliveryListItem>[] = [
    {
      key: "eventType",
      header: "Event",
      cell: (d) => <StatusBadge label={d.eventType} color={WEBHOOK_EVENT_COLOR[d.eventType]} />,
    },
    {
      key: "status",
      header: "Status",
      cell: (d) => <StatusBadge label={formatStatusLabel(d.status)} color={WEBHOOK_DELIVERY_STATUS_COLOR[d.status]}/>,
    },
    { key: "attemptCount", header: "Attempts", cell: (d) => d.attemptCount },
    { key: "lastResponseCode", header: "Last response", cell: (d) => d.lastResponseCode ?? "—" },
    { key: "nextAttemptAt", header: "Next attempt", cell: (d) => formatTimestamp(d.nextAttemptAt) },
    {
      key: "deliveredAt",
      header: "Delivered",
      cell: (d) => (d.deliveredAt ? formatTimestamp(d.deliveredAt) : "—"),
    },
  ];

  return (
    <div className="space-y-6">
      <SectionCard title="Webhook" description="Merchant-configured callback endpoint.">
        {webhookPending ? (
          <BlockStatus label="Loading webhook…"/>
        ) : webhookError ? (
          <BlockStatus label="Failed to load webhook."/>
        ) : !webhook ? (
          <BlockStatus label="No webhook configured."/>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm">{webhook.callbackUrl}</span>
              <CopyButton value={webhook.callbackUrl} label="Copy callback URL"/>
            </div>
            <StatusBadge
              label={webhook.enabled ? "Enabled" : "Disabled"}
              color={webhook.enabled ? "success" : "neutral"}
            />
          </div>
        )}
      </SectionCard>

      <SectionCard title="API keys" description="Keys this merchant uses to authenticate against the integration API.">
        {apiKeysPending ? (
          <BlockStatus label="Loading API keys…"/>
        ) : apiKeysError || !apiKeys ? (
          <BlockStatus label="Failed to load API keys."/>
        ) : apiKeys.length === 0 ? (
          <BlockStatus label="No API keys yet."/>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead>
              <tr className="bg-muted text-left text-xs font-semibold text-muted-foreground">
                <th className="px-4 py-2.5 font-semibold">Key ID</th>
                <th className="px-4 py-2.5 font-semibold">Label</th>
                <th className="px-4 py-2.5 font-semibold">Scopes</th>
                <th className="px-4 py-2.5 font-semibold">IP allowlist</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Last used</th>
                <th className="px-4 py-2.5 font-semibold">Revoked</th>
              </tr>
              </thead>
              <tbody>
              {apiKeys.map((key: MerchantApiKeyItem) => (
                <tr key={key.id} className="border-t">
                  <td className="px-4 py-2 font-mono text-xs">{key.keyId}</td>
                  <td className="px-4 py-2 font-medium">{key.label}</td>
                  <td className="px-4 py-2 text-muted-foreground">{key.scopes.join(", ")}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {key.ipAllowlist.length > 0 ? key.ipAllowlist.join(", ") : "Any"}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge label={formatStatusLabel(key.status)} color={API_KEY_STATUS_COLOR[key.status]}/>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {key.lastUsedAt ? formatTimestamp(key.lastUsedAt) : "—"}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {key.revokedAt ? formatTimestamp(key.revokedAt) : "—"}
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Delivery attempts" description="Recent webhook delivery attempts for this merchant.">
        <DataTable
          searchable={false}
          columns={deliveryColumns}
          items={deliveries?.data ?? []}
          total={deliveries?.total ?? 0}
          page={state.page}
          pageSize={state.pageSize}
          loading={deliveriesPending}
          error={deliveriesError ? "Failed to load webhook deliveries." : null}
          onPageChange={(page) => setState((s) => ({ ...s, page }))}
          onPageSizeChange={(pageSize) => setState((s) => ({ ...s, pageSize, page: 1 }))}
          getRowKey={(d) => d.id}
          emptyMessage="No webhook deliveries yet."
        />
      </SectionCard>
    </div>
  );
}

// ─── Activity tab (MERCHANTS_VIEWER) ────────────────────────────────────────

function describeActivityEvent(event: MerchantActivityEvent): string {
  switch (event.type) {
    case "INVOICE_RESOLUTION":
      return `Invoice ${event.invoicePublicId} resolved by ${event.resolvedBy} from ${
        event.fromSubStatus ?? "no sub-status"
      }`;
    case "AML_DECISION":
      return `${event.adminDisplayName} — ${event.action} — ${event.invoicePublicId}${
        event.note ? ` (${event.note})` : ""
      }`;
    case "REFUND_CREATED":
      return `Refund created (${event.initiator}) for ${event.invoicePublicId}`;
    case "REFUND_CLAIMED":
      return `Refund claimed by ${event.claimant}, ${event.amount} — ${event.invoicePublicId}`;
    default:
      return "";
  }
}

function ActivityTab({ merchantId }: { merchantId: string }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["merchant", merchantId, "activity"],
    queryFn: () => getMerchantActivity(merchantId),
  });

  if (isPending) return <BlockStatus label="Loading activity…"/>;
  if (isError || !data) return <BlockStatus label="Failed to load activity."/>;
  if (data.events.length === 0) return <BlockStatus label="No activity yet."/>;

  return (
    <ol className="space-y-3">
      {data.events.map((event, index) => (
        <li key={index} className="flex flex-wrap gap-3 rounded-lg border px-4 py-3 text-sm">
          <span className="shrink-0 text-muted-foreground" style={{ fontVariantNumeric: "tabular-nums" }}>
            {formatTimestamp(event.at)}
          </span>
          <span>{describeActivityEvent(event)}</span>
        </li>
      ))}
    </ol>
  );
}

// ─── tab dispatch + page ─────────────────────────────────────────────────────

function TabContent({
                      tabKey,
                      merchant,
                      merchantId,
                      roles,
                    }: {
  tabKey: TabKey;
  merchant: MerchantResponse;
  merchantId: string;
  roles: AdminRole[];
}) {
  switch (tabKey) {
    case "overview":
      return <OverviewTab merchant={merchant} merchantId={merchantId} roles={roles}/>;
    case "transient-wallets":
      return <DepositWalletsTab merchantId={merchantId}/>;
    case "accounts":
      return <LedgerTab merchantId={merchantId} roles={roles}/>;
    case "invoices":
      return <InvoicesTab merchantId={merchantId} roles={roles}/>;
    case "withdrawals":
      return <WithdrawalsTab merchantId={merchantId}/>;
    case "assets":
      return <AssetsTab merchantId={merchantId} roles={roles}/>;
    case "policies":
      return <PoliciesTab merchantId={merchantId}/>;
    case "api-keys":
      return <ApiKeysTab merchantId={merchantId}/>;
    case "activity":
      return <ActivityTab merchantId={merchantId}/>;
    default:
      return null;
  }
}

function MerchantDetailPage() {
  const { admin } = Route.useRouteContext();
  const { merchantId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const visibleTabs = useMemo(
    () =>
      TAB_CONFIGS.filter((tab) =>
        tab.altRole ? hasRole(admin.roles, tab.role, tab.altRole) : hasRole(admin.roles, tab.role),
      ),
    [admin.roles],
  );
  const requestedTab = search.tab as TabKey | undefined;
  const activeTab: TabKey =
    (requestedTab && visibleTabs.some((tab) => tab.key === requestedTab)
      ? requestedTab
      : visibleTabs[0]?.key) ?? "overview";

  const setTab = (key: TabKey) => {
    navigate({ search: (prev) => ({ ...prev, tab: key }) });
  };

  const {
    data: merchant,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["merchant", merchantId],
    queryFn: () => getMerchant(merchantId),
  });

  return (
    <div className="space-y-6">
      {isPending ? (
        <BlockStatus label="Loading merchant…"/>
      ) : isError || !merchant ? (
        <BlockStatus label="Failed to load merchant."/>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{merchant.businessName}</h2>
            <p className="text-sm text-muted-foreground">{merchant.email}</p>
          </div>
          <StatusBadge label={formatStatusLabel(merchant.status)} color={MERCHANT_STATUS_COLOR[merchant.status]}/>
        </div>
      )}

      {visibleTabs.length > 0 && <TabNav tabs={visibleTabs} active={activeTab} onChange={setTab}/>}

      {merchant && (
        <TabContent tabKey={activeTab} merchant={merchant} merchantId={merchantId} roles={admin.roles}/>
      )}
    </div>
  );
}
